import { InspectionPlanningPanel } from "../../components/site-workflows/InspectionPlanning"
import { CorrectiveActionsPanel } from "../../components/site-workflows/CorrectiveActions"
import { InspectionComparisonPanel } from "../../components/site-workflows/InspectionComparison"
import { useRegulatorySession } from "../../providers/regulatoryProvider"
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from "antd"
import { EditOutlined, PlusOutlined } from "@ant-design/icons"
import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import type { PhysicalKind } from "../../types/physical"
import { IdentityEditor } from "../../components/physical/IdentityEditor"
import { usePhysicalResource } from "../../components/physical/usePhysicalResource"
import { physicalApi, physicalError } from "../../utils/physicalApi"
import { PhysicalHistory } from "./PhysicalHistory"
import { PhysicalInventory } from "./PhysicalInventory"
import { PhysicalInspectionCreate } from "./PhysicalInspectionCreate"
import "./physical.css"
export function PhysicalIdentityPage({ kind }: { kind: PhysicalKind }) {
  const { uuid } = useParams<{ uuid: string }>()
  const { sessionKey } = useRegulatorySession()
  return <IdentityDetail key={`${sessionKey}/${kind}/${uuid}`} kind={kind} id={uuid} />
}
function IdentityDetail({ kind, id }: { kind: PhysicalKind; id: string }) {
  const resource = usePhysicalResource(`identity/${kind}/${id}`, (signal) =>
    physicalApi.get(kind, id, signal),
  )
  const [editing, setEditing] = useState(false)
  const [creating, setCreating] = useState(false)
  const value = resource.data
  if (resource.error)
    return (
      <Alert
        type="error"
        message={physicalError(resource.error)}
        action={<Button onClick={resource.reload}>Réessayer</Button>}
      />
    )
  if (!value) return <Spin />
  const tabs = [
    {
      key: "inspections",
      label: "Inspections",
      children: <PhysicalHistory key={value.uuid} kind={kind} id={value.uuid} />,
    },
    ...(kind !== "component"
      ? [
          {
            key: "inventory",
            label: kind === "site" ? "Équipements" : "Composants",
            children: (
              <PhysicalInventory kind={kind === "site" ? "asset" : "component"} parent={value} />
            ),
          },
        ]
      : []),
    ...(kind !== "site" && value.physicalSiteUuid
      ? [
          {
            key: "actions",
            label: "Suivi des actions",
            children: (
              <CorrectiveActionsPanel
                site={value.physicalSiteUuid}
                enterprise={value.enterpriseUuid}
                asset={kind === "asset" ? value.uuid : value.physicalAssetUuid}
                component={kind === "component" ? value.uuid : undefined}
              />
            ),
          },
        ]
      : []),
    ...(kind === "site"
      ? [
          {
            key: "planning",
            label: "Planning",
            children: (
              <InspectionPlanningPanel site={value.uuid} enterprise={value.enterpriseUuid} />
            ),
          },
          {
            key: "actions",
            label: "Suivi des actions",
            children: (
              <CorrectiveActionsPanel site={value.uuid} enterprise={value.enterpriseUuid} />
            ),
          },
          {
            key: "comparisons",
            label: "Comparaisons",
            children: <InspectionComparisonPanel site={value.uuid} />,
          },
        ]
      : []),
    {
      key: "notes",
      label: "Notes",
      children: (
        <Card title="Notes du dossier">
          <div className="physical-notes">
            {value.notes || "Aucune note. Utilisez Modifier pour compléter ce dossier."}
          </div>
        </Card>
      ),
    },
  ]
  return (
    <main className="physical-workspace">
      <Breadcrumb
        items={[
          { title: <Link to="/physical-sites">Gestionnaire de Sites</Link> },
          ...(kind !== "site" && value.physicalSiteUuid
            ? [
                {
                  title: <Link to={`/physical-sites/${value.physicalSiteUuid}`}>Site</Link>,
                },
              ]
            : []),
          ...(kind === "component" && value.physicalAssetUuid
            ? [
                {
                  title: (
                    <Link to={`/physical-assets/${value.physicalAssetUuid}`}>
                      Équipement physique
                    </Link>
                  ),
                },
              ]
            : []),
          { title: value.name },
        ]}
      />
      <div className="physical-heading">
        <div>
          <Typography.Title level={2}>{value.name}</Typography.Title>
          <Space>
            {value.code && <Tag>{value.code}</Tag>}
            <Tag>
              {kind === "site"
                ? "Site"
                : kind === "asset"
                  ? "Équipement physique"
                  : "Composant physique"}
            </Tag>
            {value.archived && <Tag color="orange">Archivé</Tag>}
          </Space>
        </div>
        <Space wrap>
          <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
            Modifier
          </Button>
          {kind === "site" && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={value.archived}
              onClick={() => setCreating(true)}
            >
              Nouvelle inspection
            </Button>
          )}
        </Space>
      </div>
      {value.archived && (
        <Alert type="info" message="Ce dossier est archivé. Son historique reste consultable." />
      )}
      <Descriptions
        bordered
        size="small"
        column={{ xs: 1, sm: 2, md: 3 }}
        items={[
          ...(kind === "site"
            ? [
                {
                  key: "address",
                  label: "Adresse actuelle",
                  children: value.address || "Non renseignée",
                },
              ]
            : []),
          ...(kind === "asset"
            ? [
                { key: "manufacturer", label: "Fabricant", children: value.manufacturer || "—" },
                { key: "model", label: "Modèle", children: value.model || "—" },
                { key: "serial", label: "N° de série", children: value.serialNumber || "—" },
              ]
            : []),
          {
            key: "updated",
            label: "Dossier mis à jour",
            children: new Date(value.updatedAt).toLocaleDateString("fr-FR"),
          },
        ]}
      />
      <Tabs items={tabs} defaultActiveKey="inspections" destroyInactiveTabPane />
      {editing && (
        <IdentityEditor
          kind={kind}
          value={value}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            resource.reload()
          }}
        />
      )}
      {creating && <PhysicalInspectionCreate site={value} onCancel={() => setCreating(false)} />}
    </main>
  )
}
